// src/pages/app/CashAdvancesPage.jsx — Cash advances management
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { cashAdvancesApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, CheckCircle2, XCircle, RefreshCw, Clock, AlertTriangle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  reconciled: 'bg-slate-100 text-slate-700',
};

export default function CashAdvancesPage() {
  const { isRole } = useAuth();
  const [advances, setAdvances] = useState([]);
  const [aging, setAging] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [actionLoading, setActionLoading] = useState({});
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ requested_amount: '', purpose: '', currency: 'INR' });
  const [submitting, setSubmitting] = useState(false);

  const [approveModal, setApproveModal] = useState({ open: false, id: null, amount: '', comment: 'Approved' });
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, comment: '' });
  const [reconcileModal, setReconcileModal] = useState({ open: false, id: null, amount: '' });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await cashAdvancesApi.list();
      setAdvances(data);
      if (isRole('finance', 'admin', 'manager')) {
        const { data: agingData } = await cashAdvancesApi.aging();
        setAging(agingData);
      }
    } catch { toast.error('Failed to load cash advances'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleApproveSubmit = async () => {
    if (!approveModal.id) return;
    const { id, amount, comment } = approveModal;
    setActionLoading(p => ({ ...p, [id]: true }));
    try {
      await cashAdvancesApi.approve(id, {
        approved_amount: amount ? parseFloat(amount) : undefined,
        comment: comment || 'Approved'
      });
      toast.success('Cash advance approved!');
      setApproveModal({ open: false, id: null, amount: '', comment: 'Approved' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approval failed'); }
    finally { setActionLoading(p => ({ ...p, [id]: false })); }
  };

  const handleRejectSubmit = async () => {
    if (!rejectModal.id || !rejectModal.comment.trim()) return;
    const { id, comment } = rejectModal;
    setActionLoading(p => ({ ...p, [id]: true }));
    try {
      await cashAdvancesApi.reject(id, { comment });
      toast.success('Rejected');
      setRejectModal({ open: false, id: null, comment: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rejection failed'); }
    finally { setActionLoading(p => ({ ...p, [id]: false })); }
  };

  const handleReconcileSubmit = async () => {
    if (!reconcileModal.id) return;
    const { id, amount } = reconcileModal;
    setActionLoading(p => ({ ...p, [id]: true }));
    try {
      await cashAdvancesApi.reconcile(id, { reconciled_amount: parseFloat(amount || 0) });
      toast.success('Advance reconciled!');
      setReconcileModal({ open: false, id: null, amount: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Reconciliation failed'); }
    finally { setActionLoading(p => ({ ...p, [id]: false })); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await cashAdvancesApi.create({ ...newForm, requested_amount: parseFloat(newForm.requested_amount) });
      toast.success('Cash advance requested!');
      setShowNewForm(false);
      setNewForm({ requested_amount: '', purpose: '', currency: 'INR' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create advance'); }
    finally { setSubmitting(false); }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">Cash Advances</h1>
          <p className="text-slate-500 text-sm mt-1">Pre-trip funding requests and reconciliation</p>
        </div>
        {isRole('employee', 'admin') && (
          <button onClick={() => setShowNewForm(!showNewForm)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors">
            <Plus className="w-4 h-4" /> Request Advance
          </button>
        )}
      </div>

      {/* New advance form */}
      {showNewForm && (
        <Card>
          <CardHeader><CardTitle className="text-sm sm:text-base">Request Cash Advance</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Amount *</label>
                <input required type="number" min="1" value={newForm.requested_amount}
                  onChange={e => setNewForm(f => ({ ...f, requested_amount: e.target.value }))}
                  placeholder="10000" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Currency</label>
                <Select value={newForm.currency} onValueChange={val => setNewForm(f => ({ ...f, currency: val }))}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {['INR', 'USD', 'EUR', 'GBP', 'AED'].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Purpose *</label>
                <textarea required rows={2} value={newForm.purpose}
                  onChange={e => setNewForm(f => ({ ...f, purpose: e.target.value }))}
                  placeholder="Describe why you need this advance..." className={`${inputCls} resize-none`} />
              </div>
              <div className="sm:col-span-2 flex gap-3">
                <button type="button" onClick={() => setShowNewForm(false)}
                  className="px-5 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Submit Request
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      {isRole('finance', 'admin', 'manager') && (
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
          {['all', 'aging'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={cn("px-4 py-1.5 rounded-md text-sm font-medium transition-colors capitalize",
                tab === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}>
              {t === 'aging' ? 'Aging Report' : 'All Advances'}
            </button>
          ))}
        </div>
      )}

      {/* Aging alerts */}
      {tab === 'aging' && aging.some(a => a.days_outstanding > 30) && (
        <div className="flex items-center gap-2 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>
            <strong>{aging.filter(a => a.days_outstanding > 30).length}</strong> advances are overdue (30+ days outstanding). Reminders should be sent.
          </span>
        </div>
      )}

      {/* List */}
      <Card className="border-slate-200 bg-white overflow-hidden shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" /></div>
          ) : (() => {
            const items = tab === 'aging' ? aging : advances;
            return !items.length ? (
              <div className="text-center py-16 text-slate-400 text-sm">No {tab === 'aging' ? 'outstanding' : ''} advances found</div>
            ) : (
              <div className="overflow-x-auto">
                <div className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between text-xs text-slate-500 font-medium">
                  <span>Showing <strong>{items.length}</strong> cash advance{items.length === 1 ? '' : 's'}</span>
                </div>
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {(tab === 'aging'
                        ? ['Employee', 'Department', 'Amount Approved', 'Approved Date', 'Days Outstanding', 'Trip']
                        : ['Employee', 'Requested Amount', 'Purpose', 'Status', 'Actions']
                      ).map((h, i) => (
                        <th key={h} className={cn("px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3", i === (tab === 'aging' ? 5 : 4) ? "text-right" : "")}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map(adv => (
                      <tr key={adv.id} className="hover:bg-slate-50/80 transition-colors group">
                        <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-700 font-bold flex items-center justify-center text-xs shrink-0">
                              {adv.employee_name?.charAt(0) || 'E'}
                            </div>
                            <span className="font-semibold text-slate-900">{adv.employee_name}</span>
                          </div>
                        </td>
                        {tab === 'aging' ? (
                          <>
                            <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-xs text-slate-600 font-medium">{adv.department || '—'}</td>
                            <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900 whitespace-nowrap">₹{parseFloat(adv.approved_amount || 0).toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-xs text-slate-600 whitespace-nowrap">{adv.approved_at ? new Date(adv.approved_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                            <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 whitespace-nowrap">
                              <span className={cn("inline-flex items-center gap-1.5 text-xs px-2.5 py-0.5 rounded-full font-semibold border",
                                adv.days_outstanding > 30 ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200")}>
                                <Clock className="w-3 h-3" />
                                {Math.round(adv.days_outstanding || 0)} days
                              </span>
                            </td>
                            <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-right text-xs text-slate-600 font-medium">{adv.trip_destination || '—'}</td>
                          </>
                        ) : (
                          <>
                            <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900 whitespace-nowrap">{adv.currency} {parseFloat(adv.requested_amount).toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-xs text-slate-600 max-w-[220px] truncate">{adv.purpose}</td>
                            <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 whitespace-nowrap">
                              <span className={cn("inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium border capitalize", STATUS_COLORS[adv.status])}>
                                <span className={cn("w-1.5 h-1.5 rounded-full",
                                  adv.status === 'approved' ? 'bg-emerald-500' :
                                  adv.status === 'reconciled' ? 'bg-slate-500' :
                                  adv.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'
                                )} />
                                {adv.status}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-right whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1.5">
                                {isRole('manager', 'finance', 'admin') && adv.status === 'pending' && (
                                  <>
                                    <button onClick={() => setApproveModal({ open: true, id: adv.id, amount: adv.requested_amount, comment: 'Approved' })} disabled={actionLoading[adv.id]}
                                      className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-50" title="Approve">
                                      <CheckCircle2 className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => setRejectModal({ open: true, id: adv.id, comment: '' })} disabled={actionLoading[adv.id]}
                                      className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50" title="Reject">
                                      <XCircle className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                                {adv.status === 'approved' && !adv.reconciled_at && (
                                  <button onClick={() => setReconcileModal({ open: true, id: adv.id, amount: adv.approved_amount || adv.requested_amount })} disabled={actionLoading[adv.id]}
                                    className="px-2.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold inline-flex items-center gap-1 shadow-sm transition-colors" title="Reconcile">
                                    <RefreshCw className="w-3.5 h-3.5" /> Reconcile
                                  </button>
                                )}
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Approve Modal */}
      <Dialog open={approveModal.open} onOpenChange={open => setApproveModal(p => ({ ...p, open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold text-slate-900">Approve Cash Advance</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Set approved amount and optional approval notes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Approved Amount</label>
              <input type="number" value={approveModal.amount} onChange={e => setApproveModal(p => ({ ...p, amount: e.target.value }))} placeholder="Amount" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Comment</label>
              <input type="text" value={approveModal.comment} onChange={e => setApproveModal(p => ({ ...p, comment: e.target.value }))} placeholder="Approval comment" className={inputCls} />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button type="button" onClick={() => setApproveModal(p => ({ ...p, open: false }))} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
            <button type="button" onClick={handleApproveSubmit} disabled={actionLoading[approveModal.id]} className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50">Approve</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectModal.open} onOpenChange={open => setRejectModal(p => ({ ...p, open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold text-slate-900">Reject Cash Advance</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Please state the reason for rejecting this cash advance.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Rejection Reason *</label>
              <textarea rows={3} value={rejectModal.comment} onChange={e => setRejectModal(p => ({ ...p, comment: e.target.value }))} placeholder="Reason for rejection..." className={`${inputCls} resize-none`} />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button type="button" onClick={() => setRejectModal(p => ({ ...p, open: false }))} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
            <button type="button" onClick={handleRejectSubmit} disabled={!rejectModal.comment.trim() || actionLoading[rejectModal.id]} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">Reject</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reconcile Modal */}
      <Dialog open={reconcileModal.open} onOpenChange={open => setReconcileModal(p => ({ ...p, open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold text-slate-900">Reconcile Cash Advance</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Enter amount actually spent. Returned unspent amount is automatically implied.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Amount Actually Spent</label>
              <input type="number" min="0" value={reconcileModal.amount} onChange={e => setReconcileModal(p => ({ ...p, amount: e.target.value }))} placeholder="Spent amount" className={inputCls} />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button type="button" onClick={() => setReconcileModal(p => ({ ...p, open: false }))} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
            <button type="button" onClick={handleReconcileSubmit} disabled={actionLoading[reconcileModal.id]} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">Confirm Reconciliation</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
